import { useRef, useState } from "react";
import { FileUp, RefreshCw, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DataTable, type DataTableColumn } from "@/components/cinch/DataTable/DataTable";
import { IconButton } from "@/components/cinch/IconButton";
import { StatusBadge } from "@/components/cinch/StatusBadge";

import {
  fmtQty,
  monthLabel,
  useAiConfig,
  useAiReview,
  useCosPeriod,
  useCosUploads,
  useDeleteCosUpload,
  useReconSyncStatus,
  useReparseCosUpload,
  useRunAiReview,
  useSyncRecon,
  useUploadCosFiles,
  type UploadRead,
} from "../api";

const MODEL_LABEL: Record<string, string> = {
  "claude-opus-5": "Claude Opus 5 (recommended)",
  "claude-opus-4-8": "Claude Opus 4.8",
  "claude-sonnet-5": "Claude Sonnet 5",
  "claude-haiku-4-5": "Claude Haiku 4.5",
};

/**
 * Upload the month's cost files (.xlsx) and track each file's ingestion.
 * Re-uploading a same-named file supersedes it; a differently named file adds
 * rows (the Cost Report workbook + the mailer costs file live side by side).
 */
export function UploadsView({ period }: { period: string }) {
  const { data: detail } = useCosPeriod(period);
  const exists = detail?.exists ?? false;
  const signedOff = detail?.status === "signed_off";
  const uploads = useCosUploads(period, exists);
  const upload = useUploadCosFiles(period);
  const reparse = useReparseCosUpload(period);
  const remove = useDeleteCosUpload(period);
  const inputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const aiConfig = useAiConfig();
  const [model, setModel] = useState<string | null>(null);
  const chosenModel = model ?? aiConfig.data?.default_model ?? "claude-opus-5";

  const submit = () => {
    if (files.length === 0) return;
    upload.mutate({ files, model: chosenModel }, {
      onSuccess: (rows) => {
        const failed = rows.filter((r) => r.status === "failed");
        if (failed.length) {
          toast.warning(`${failed.length} file${failed.length === 1 ? "" : "s"} failed to parse`, {
            description: failed.map((f) => f.filename).join(", "),
          });
        } else {
          toast.success(
            `Ingested ${files.length} file${files.length === 1 ? "" : "s"} into ${monthLabel(period)}`,
          );
        }
        setFiles([]);
        if (inputRef.current) inputRef.current.value = "";
      },
      onError: (e) => toast.error("Upload failed", { description: (e as Error).message }),
    });
  };

  const columns: DataTableColumn<UploadRead>[] = [
    {
      key: "filename",
      header: "File",
      render: (u) => (
        <div className="max-w-[320px]">
          <div className="truncate font-medium" title={u.filename}>
            {u.filename}
          </div>
          <div className="text-xs text-muted-foreground">
            {u.kind === "invoice"
              ? `Vendor invoice · ${u.parser_version?.replace("ai:", "") ?? "AI"}`
              : u.kind === "recon"
                ? "Pulled from Integration Reconciliation"
                : u.kind === "mailer"
                  ? "Mailer costs file"
                  : "Cost report workbook"}
          </div>
        </div>
      ),
    },
    { key: "period", header: "Cost month", render: (u) => monthLabel(u.period) },
    {
      key: "status",
      header: "Ingestion",
      render: (u) => <StatusBadge value={u.status} />,
    },
    {
      key: "rows",
      header: "Rows",
      align: "right",
      numeric: true,
      render: (u) => fmtQty(u.row_count),
    },
    {
      key: "message",
      header: "Notes",
      render: (u) =>
        u.message ? (
          <span
            className="block max-w-[280px] truncate text-xs text-muted-foreground"
            title={u.message}
          >
            {u.message}
          </span>
        ) : (
          "—"
        ),
    },
    {
      key: "who",
      header: "Uploaded",
      render: (u) => (
        <div className="text-xs">
          <div>{u.uploaded_by_name ?? "—"}</div>
          <div className="text-muted-foreground">
            {new Date(u.created_at).toLocaleString()}
          </div>
        </div>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (u) => (
        <div className="flex justify-end gap-1">
          <IconButton
            title="Re-parse the stored file"
            disabled={signedOff || reparse.isPending}
            onClick={() =>
              reparse.mutate(u.id, {
                onSuccess: (row) =>
                  row.status === "failed"
                    ? toast.warning("Re-parse failed", { description: row.message ?? undefined })
                    : toast.success(`Re-parsed ${u.filename}`),
                onError: (e) =>
                  toast.error("Re-parse failed", { description: (e as Error).message }),
              })
            }
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </IconButton>
          <IconButton
            title="Delete this upload and its rows"
            disabled={signedOff || remove.isPending}
            onClick={() =>
              remove.mutate(u.id, {
                onSuccess: () => toast.success(`Removed ${u.filename}`),
                onError: (e) =>
                  toast.error("Delete failed", { description: (e as Error).message }),
              })
            }
          >
            <Trash2 className="h-3.5 w-3.5" />
          </IconButton>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            Upload {monthLabel(period)}&apos;s vendor invoices
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Drop each vendor&apos;s invoice as it arrives — PDF, CSV, or image. Claude
            extracts the vendor, invoice number, and charge lines, and the mapping rules
            route them to product lines automatically. Spreadsheets (.xlsx) still work:
            the mailer costs file, or a legacy Cost Report workbook. Same-named
            re-uploads supersede; the Vendors tab tracks which invoices are still missing.
          </p>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3">
          <Input
            id="cos-upload-files"
            ref={inputRef}
            type="file"
            accept=".pdf,.csv,.txt,.png,.jpg,.jpeg,.xlsx,.xlsm"
            multiple
            disabled={signedOff}
            onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
            className="w-96"
          />
          <div className="flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
              Extraction model
            </span>
            <Select value={chosenModel} onValueChange={setModel}>
              <SelectTrigger className="h-9 w-[230px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(aiConfig.data?.models ?? ["claude-opus-5"]).map((m) => (
                  <SelectItem key={m} value={m}>
                    {MODEL_LABEL[m] ?? m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button disabled={signedOff || files.length === 0 || upload.isPending} onClick={submit}>
            <FileUp className="mr-1.5 h-4 w-4" />
            {upload.isPending
              ? "Ingesting…"
              : `Ingest ${files.length || ""} file${files.length === 1 ? "" : "s"}`}
          </Button>
          {aiConfig.data && !aiConfig.data.key_configured && (
            <p className="text-sm text-warning">
              No Anthropic API key configured — PDF/CSV invoice extraction won&apos;t run.
              Add <code>anthropic_api_key</code> in Settings.
            </p>
          )}
          {signedOff && (
            <p className="text-sm text-warning">
              {monthLabel(period)} is signed off — reopen it on the Details tab to change its files.
            </p>
          )}
        </CardContent>
      </Card>

      <ReconSyncCard period={period} signedOff={signedOff} />

      <AiReviewCard period={period} model={chosenModel} signedOff={signedOff} />

      <DataTable
        columns={columns}
        rows={uploads.data ?? []}
        rowKey={(u) => u.id}
        loading={exists && uploads.isLoading}
        emptyMessage={
          exists
            ? "No files uploaded for this month yet."
            : "This month hasn't been opened — uploading a file opens it."
        }
      />
    </div>
  );
}


function AiReviewCard({
  period,
  model,
  signedOff,
}: {
  period: string;
  model: string;
  signedOff: boolean;
}) {
  const { data: detail } = useCosPeriod(period);
  const exists = detail?.exists ?? false;
  const review = useAiReview(period, exists);
  const run = useRunAiReview(period);
  const config = useAiConfig();

  const severityBadge = (severity: string) =>
    severity === "action" ? (
      <StatusBadge value="flagged" label="Action" />
    ) : severity === "warn" ? (
      <StatusBadge value="partial" label="Watch" />
    ) : (
      <Badge variant="secondary">Info</Badge>
    );

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-3">
        <div>
          <CardTitle className="text-base">AI review</CardTitle>
          <p className="text-sm text-muted-foreground">
            Claude reads the month — movements vs prior months, missing invoices,
            unmapped rows — and recommends what to check before sign-off.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={!exists || run.isPending || config.data?.key_configured === false}
          onClick={() =>
            run.mutate(model, {
              onSuccess: () => toast.success("AI review complete"),
              onError: (e) =>
                toast.error("AI review failed", { description: (e as Error).message }),
            })
          }
        >
          <Sparkles className="mr-1.5 h-3.5 w-3.5" />
          {run.isPending
            ? "Reviewing…"
            : review.data
              ? "Run again"
              : `Review ${monthLabel(period)}`}
        </Button>
      </CardHeader>
      <CardContent>
        {!exists ? (
          <p className="text-sm text-muted-foreground">
            Upload this month&apos;s files first.
          </p>
        ) : run.isPending ? (
          <div className="h-24 animate-pulse rounded-md bg-muted/40" />
        ) : !review.data ? (
          <p className="text-sm text-muted-foreground">
            No review yet{signedOff ? " — you can still run one on a signed-off month" : ""}.
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            <p className="text-sm">{review.data.summary}</p>
            <div className="flex flex-col gap-2">
              {review.data.recommendations.map((r, i) => (
                <div key={i} className="flex items-start gap-2 rounded-md border border-border p-2.5">
                  <div className="pt-0.5">{severityBadge(r.severity)}</div>
                  <div>
                    <div className="text-sm font-medium">{r.title}</div>
                    {r.detail && (
                      <div className="text-xs text-muted-foreground">{r.detail}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              {review.data.model} · {new Date(review.data.created_at).toLocaleString()}
              {review.data.created_by_name ? ` · by ${review.data.created_by_name}` : ""}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function ReconSyncCard({ period, signedOff }: { period: string; signedOff: boolean }) {
  const status = useReconSyncStatus(period);
  const sync = useSyncRecon(period);
  if (!status.data?.available) return null;
  return (
    <Card>
      <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-6">
        <div>
          <div className="text-sm font-medium">
            Integration Expense Reconciliation has {status.data.invoice_count} invoice
            {status.data.invoice_count === 1 ? "" : "s"} for {monthLabel(period)}
          </div>
          <p className="text-xs text-muted-foreground">
            Pull them in as line items — entered once over there, counted here. Invoices
            already uploaded to this month are skipped automatically.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={signedOff || sync.isPending}
          onClick={() =>
            sync.mutate(undefined, {
              onSuccess: (r) => {
                const skipped = r.skipped.length
                  ? ` · ${r.skipped.length} skipped (already here)`
                  : "";
                toast.success(`Pulled ${r.created} invoice${r.created === 1 ? "" : "s"} from R-18${skipped}`);
              },
              onError: (e) =>
                toast.error("Sync failed", { description: (e as Error).message }),
            })
          }
        >
          {sync.isPending ? "Pulling…" : "Pull from R-18"}
        </Button>
      </CardContent>
    </Card>
  );
}
